/* ---------------------------------------------------------------------------
   Where a bridge is in its life: planned, under construction, in service or
   closed, and whether an in-service bridge is restricted. LIFECYCLE.md §3 is
   the specification; this is it in code.

   ONE FILE, BOTH SIDES, as src/data/roles.ts is. functions/lib/phases.ts
   refuses a transition this table does not allow, and the screens read the same
   table to decide which applications a bridge offers. Two copies would drift,
   and the drift that matters is the server letting a bridge under construction
   be closed by a decision the screens never offered.

   THE PHASE IS STORED, NEVER INFERRED (§3). `year` and `loadRestriction` stay
   what they are and neither implies a phase. Every transition is a row in
   `bridge_phases` (migration 0035) and the current phase is the latest row. A
   bridge with no row is in service, marked *not recorded* — every register
   imported before this existed is in that state, and saying "in service"
   without the mark would be the register asserting what nobody recorded.

   RESTRICTED IS A FLAG ON IN-SERVICE, NOT A PHASE. Four phases and a flag make
   the five states the screens show. A restricted bridge is still in service:
   it is inspected and rated like any other, and only its posting differs.

   MOST BRIDGES ARE ALREADY BUILT (§3, *Bridges that already exist*). One is
   recorded straight into service (`register`, LC-80), never through planned
   and construction; and works on it — a rehabilitation, a widening, a deck
   replaced — are an OVERLAY on the phase, not a phase (LC-81). The works kind
   rides on each `bridge_phases` row as the flag does, set by the row that
   accepted the works baseline and cleared by the one that accepted the works
   completion, so *every transition is a row* holds for them too.
   --------------------------------------------------------------------------- */

import type { Capability } from './roles.ts';

export const PHASES = ['planned', 'construction', 'in-service', 'closed'] as const;
export type Phase = (typeof PHASES)[number];

/** The five states users see: the four phases, with in-service split by the flag. */
export type PhaseStateKey = 'planned' | 'construction' | 'in-service' | 'restricted' | 'closed';

export const STATE_KEYS: readonly PhaseStateKey[] = [
  'planned',
  'construction',
  'in-service',
  'restricted',
  'closed',
];

/* --------------------------- works on a bridge in service --------------------------- */

export const WORKS_KINDS = ['rehabilitation', 'strengthening', 'widening', 'deck-replacement', 'repair', 'other'] as const;
export type WorksKind = (typeof WORKS_KINDS)[number];

export const WORKS_KIND_LABEL: Readonly<Record<WorksKind, string>> = {
  rehabilitation: 'Rehabilitation',
  strengthening: 'Strengthening',
  widening: 'Widening',
  'deck-replacement': 'Deck replacement',
  repair: 'Repair',
  other: 'Other works',
};

export function isWorksKind(raw: unknown): raw is WorksKind {
  return typeof raw === 'string' && (WORKS_KINDS as readonly string[]).includes(raw);
}

/** The works open on a bridge, as `Bridge.works` carries them (LC-81). */
export interface WorksOpen {
  kind: WorksKind;
  /** When the works baseline was accepted, and by whom. */
  since: string | null;
  by: string | null;
}

/** Where a bridge stands, as read off its latest `bridge_phases` row. */
export interface PhaseState {
  phase: Phase;
  restricted: boolean;
  /** False where the bridge has no row: in service by default, not by record. */
  recorded: boolean;
  /** The works open on a bridge in service or closed, or null (LC-81). */
  works: WorksKind | null;
  /**
   * Whether a bridge built has any construction on record here: a handover
   * accepted, or works opened or completed. False for one registered straight
   * into service (LC-80) with none since.
   */
  constructed?: boolean;
}

/** The fields a `Bridge` carries for this, all optional so a seed need not. */
export interface PhaseFields {
  phase?: Phase | null;
  restricted?: boolean | null;
  works?: Pick<WorksOpen, 'kind'> | null;
  handover?: unknown;
  worksCompleted?: unknown;
}

export function isPhase(raw: unknown): raw is Phase {
  return typeof raw === 'string' && (PHASES as readonly string[]).includes(raw);
}

/** A bridge's state. No row reads as in service, not recorded. */
export function phaseOf(b: PhaseFields): PhaseState {
  if (!isPhase(b.phase)) return { phase: 'in-service', restricted: false, recorded: false, works: null };
  const built = b.phase === 'in-service' || b.phase === 'closed';
  return {
    phase: b.phase,
    restricted: b.phase === 'in-service' && b.restricted === true,
    recorded: true,
    works: built && isWorksKind(b.works?.kind) ? b.works!.kind : null,
  };
}

/**
 * A bridge's state as the screens draw its applications: `phaseOf`, with
 * whether a bridge built has any construction on record. The server never reads
 * `constructed` — it hides a tab with nothing to show and refuses nothing.
 */
export function viewPhaseOf(b: PhaseFields): PhaseState {
  const p = phaseOf(b);
  const built = p.phase === 'in-service' || p.phase === 'closed';
  return { ...p, constructed: !built || p.works !== null || b.handover != null || b.worksCompleted != null };
}

export function stateKey(p: Pick<PhaseState, 'phase' | 'restricted'>): PhaseStateKey {
  return p.phase === 'in-service' && p.restricted ? 'restricted' : p.phase;
}

export const STATE_LABEL: Readonly<Record<PhaseStateKey, string>> = {
  planned: 'Planned',
  construction: 'Under construction',
  'in-service': 'In service',
  restricted: 'Restricted',
  closed: 'Closed',
};

/** The chip's text: the state, and the mark where nothing recorded it. */
export function phaseLabel(p: PhaseState): string {
  const label = STATE_LABEL[stateKey(p)];
  return p.recorded ? label : `${label} · not recorded`;
}

/* --------------------------- transitions --------------------------- */

/**
 * The acts that move a bridge, each one row in `bridge_phases`.
 *
 * `create` makes a planned record (POST /api/bridges). `register` records a
 * bridge already built as in service (LC-80). `start` is accepting the first
 * baseline (LC-40). `handover` is accepting the handover (LC-11). `works` is
 * accepting a works baseline on a bridge in service, and `complete` accepting
 * the works completion (LC-81, LC-82): both leave the phase and its flag where
 * they are. The other four are decisions, and `restrict` is also what an
 * accepted rating that needs a posting does (LC-23).
 */
export const PHASE_ACTS = [
  'create',
  'register',
  'start',
  'handover',
  'works',
  'complete',
  'restrict',
  'lift',
  'close',
  'reopen',
] as const;
export type PhaseAct = (typeof PHASE_ACTS)[number];

/** What caused a transition — the kind of record `cause_id` names. */
export const PHASE_CAUSES = ['create', 'baseline', 'handover', 'rating', 'decision'] as const;
export type PhaseCause = (typeof PHASE_CAUSES)[number];

interface ActRule {
  /** The states it may be made from. `unrecorded` is a bridge with no row at all. */
  from: readonly (PhaseStateKey | 'unrecorded')[];
  /** Null where the act leaves the phase and its flag as they are: the works overlay. */
  to: Phase | null;
  restricted: boolean | null;
  /** What it does to the works overlay, which must be shut to open and open to close. */
  works?: 'open' | 'close';
  /** The causes it may carry, the first being its own. */
  causes: readonly PhaseCause[];
  /** A decision says why. */
  reason: boolean;
}

/**
 * §3's *Entered by* column, as rules.
 *
 * A bridge with no row is in service and may be restricted or closed like one —
 * Nepal's five and DPWH's 108 are standing structures. It cannot be started or
 * handed over: nothing recorded that it was ever planned.
 */
export const ACT_RULES: Readonly<Record<PhaseAct, ActRule>> = {
  create: { from: ['unrecorded'], to: 'planned', restricted: false, causes: ['create'], reason: false },
  register: { from: ['unrecorded'], to: 'in-service', restricted: false, causes: ['create'], reason: false },
  start: { from: ['planned'], to: 'construction', restricted: false, causes: ['baseline'], reason: false },
  handover: { from: ['construction'], to: 'in-service', restricted: false, causes: ['handover'], reason: false },
  works: { from: ['in-service', 'restricted', 'closed'], to: null, restricted: null, works: 'open', causes: ['baseline'], reason: false },
  complete: { from: ['in-service', 'restricted', 'closed'], to: null, restricted: null, works: 'close', causes: ['handover'], reason: false },
  restrict: { from: ['in-service'], to: 'in-service', restricted: true, causes: ['decision', 'rating'], reason: true },
  lift: { from: ['restricted'], to: 'in-service', restricted: false, causes: ['decision'], reason: true },
  close: { from: ['in-service', 'restricted'], to: 'closed', restricted: false, causes: ['decision'], reason: true },
  reopen: { from: ['closed'], to: 'in-service', restricted: false, causes: ['decision'], reason: true },
};

/** The acts a person decides with `status.decide`, in the order the control offers them. */
export const DECISIONS: readonly PhaseAct[] = ['restrict', 'lift', 'close', 'reopen'];

export const ACT_LABEL: Readonly<Record<PhaseAct, string>> = {
  create: 'Created as planned',
  register: 'Recorded in service',
  start: 'Construction started',
  handover: 'Handed over',
  works: 'Works opened',
  complete: 'Works completed',
  restrict: 'Restricted',
  lift: 'Restriction lifted',
  close: 'Closed',
  reopen: 'Reopened',
};

/** Which act a stored row was, from its two phases, the flag after it, and its cause. */
export function actOfRow(row: {
  fromPhase: Phase | null;
  toPhase: Phase;
  restricted: boolean;
  causeKind?: string | null;
}): PhaseAct {
  if (row.fromPhase === null) return row.toPhase === 'planned' ? 'create' : 'register';
  if (row.toPhase === 'construction') return 'start';
  if (row.fromPhase === 'construction') return 'handover';
  /* The works rows keep the phase, so only their cause tells them apart. */
  if (row.causeKind === 'baseline') return 'works';
  if (row.causeKind === 'handover') return 'complete';
  if (row.toPhase === 'closed') return 'close';
  if (row.fromPhase === 'closed') return 'reopen';
  return row.restricted ? 'restrict' : 'lift';
}

/**
 * §3's *Who* column: the capability a transition needs.
 *
 * Restricting is the one act with two holders — an approver deciding, or an
 * accepted rating that needs a posting — and which is said by its cause.
 */
export function actCapability(act: PhaseAct, cause: PhaseCause = ACT_RULES[act].causes[0]!): Capability {
  switch (act) {
    case 'create':
      return 'structure.create';
    case 'register':
      return 'inventory.edit';
    case 'start':
    case 'works':
      return 'programme.approve';
    case 'handover':
    case 'complete':
      return 'handover.accept';
    case 'restrict':
      return cause === 'rating' ? 'rating.accept' : 'status.decide';
    default:
      return 'status.decide';
  }
}

/** Whether an act may be made from where a bridge stands. */
export function actAllowed(current: PhaseState, act: PhaseAct): boolean {
  const from = current.recorded ? stateKey(current) : 'unrecorded';
  const rule = ACT_RULES[act];
  /* Works are opened while none are, and completed while they are. */
  if (rule.works === 'open' && current.works !== null) return false;
  if (rule.works === 'close' && current.works === null) return false;
  /* A bridge with no row is in service for every purpose but `create` and `register`. */
  return rule.from.includes(from) || (from === 'unrecorded' && rule.from.includes('in-service'));
}

/** The acts open from where a bridge stands, in the order the control offers them. */
export function decisionsFrom(current: PhaseState): PhaseAct[] {
  return DECISIONS.filter((act) => actAllowed(current, act));
}

/* --------------------------- the applications --------------------------- */

export type ProductId = 'inspection' | 'rating' | 'scheduler' | 'monitoring';

export type AppMode = 'open' | 'read-only' | 'hidden';

export interface AppAccess {
  mode: AppMode;
  /** §3's qualifier where it has one: "design check only", "special inspections only". */
  scope?: string;
}

/**
 * §3's table, state by application. The whole of LC-13 is this and its readers.
 *
 * THE SCHEDULER IS OPEN ON A BRIDGE BUILT (LC-81): a planner proposes a works
 * baseline on it, and an approver's acceptance opens the works. Monitoring
 * opens with them — `WORKS_ACCESS`, which `appAccess` lays over this table
 * while works are open.
 */
export const APP_ACCESS: Readonly<Record<PhaseStateKey, Readonly<Record<ProductId, AppAccess>>>> = {
  planned: {
    scheduler: { mode: 'open' },
    monitoring: { mode: 'read-only' },
    inspection: { mode: 'hidden' },
    rating: { mode: 'hidden' },
  },
  construction: {
    scheduler: { mode: 'open' },
    monitoring: { mode: 'open' },
    inspection: { mode: 'hidden', scope: 'No cycle, no alarm' },
    rating: { mode: 'open', scope: 'Design check only' },
  },
  'in-service': {
    scheduler: { mode: 'open', scope: 'Works baselines' },
    monitoring: { mode: 'read-only', scope: 'History' },
    inspection: { mode: 'open', scope: 'Cycle runs from the in-service date' },
    rating: { mode: 'open' },
  },
  restricted: {
    scheduler: { mode: 'open', scope: 'Works baselines' },
    monitoring: { mode: 'read-only' },
    inspection: { mode: 'open' },
    rating: { mode: 'open' },
  },
  closed: {
    scheduler: { mode: 'open', scope: 'Works baselines' },
    monitoring: { mode: 'read-only' },
    inspection: { mode: 'open', scope: 'Special inspections only' },
    rating: { mode: 'open' },
  },
};

/** What works open on a bridge built change in §3's table: the site record opens (LC-81). */
export const WORKS_ACCESS: Readonly<Record<'scheduler' | 'monitoring', AppAccess>> = {
  scheduler: { mode: 'open', scope: 'Works open' },
  monitoring: { mode: 'open', scope: 'Works open' },
};

/** A bridge's state as the applications read it: the phase, its flag, and any works open. */
export type AccessState = Pick<PhaseState, 'phase' | 'restricted' | 'constructed'> & { works?: WorksKind | null };

/**
 * Bridge — what the structure is, and its state — is open in every state. A
 * bridge built with no construction on record has nothing for the Scheduler or
 * Monitoring to show, and both are hidden on it.
 */
export function appAccess(p: AccessState, app: ProductId | 'structure'): AppAccess {
  if (app === 'structure') return { mode: 'open' };
  if (p.works != null && (app === 'scheduler' || app === 'monitoring')) return WORKS_ACCESS[app];
  if (p.constructed === false && (app === 'scheduler' || app === 'monitoring')) return { mode: 'hidden' };
  return APP_ACCESS[stateKey(p)][app];
}

const APP_NAME: Readonly<Record<ProductId, string>> = {
  inspection: 'Bridge Inspection',
  rating: 'Bridge Rating',
  scheduler: 'Construction Scheduler',
  monitoring: 'Construction Monitoring',
};

/**
 * The one line a hidden or read-only application shows: why, and when it opens.
 * Null where the application is open.
 */
export function accessLine(p: AccessState, app: ProductId | 'structure'): string | null {
  const access = appAccess(p, app);
  const state = STATE_LABEL[stateKey(p)].toLowerCase();
  if (app === 'structure' || access.mode === 'open') return null;
  if (access.mode === 'read-only') {
    return p.phase === 'planned'
      ? `${APP_NAME[app]} is read-only while this structure is planned — it opens when construction starts.`
      : `${APP_NAME[app]} is read-only: this structure is ${state} with no works open, and its record is kept as history.`;
  }
  if (app === 'inspection') {
    return `${APP_NAME[app]} opens at handover — this structure is ${state}.`;
  }
  if (p.constructed === false) {
    return `${APP_NAME[app]} has nothing to show — this structure is ${state} with no construction on record.`;
  }
  return `${APP_NAME[app]} opens when construction starts — this structure is ${state}.`;
}

/**
 * Which application a write capability belongs to, for the capabilities whose
 * use depends on the phase. The rest — the inventory, models, people, rates,
 * the decisions themselves — are open in every state.
 */
export const CAPABILITY_APP: Readonly<Partial<Record<Capability, ProductId>>> = {
  'programme.manage': 'scheduler',
  'programme.approve': 'scheduler',
  'progress.record': 'monitoring',
  'progress.review': 'monitoring',
  'works.supervise': 'monitoring',
  'handover.prepare': 'monitoring',
  'handover.accept': 'monitoring',
  'inspection.log': 'inspection',
  'inspection.accept': 'inspection',
  'defect.log': 'inspection',
  'defect.progress': 'inspection',
  'defect.verify': 'inspection',
  'defect.withdraw': 'inspection',
  'rating.compute': 'rating',
  'rating.accept': 'rating',
};

/** Whether this state lets a capability be used on the bridge: its application is open. */
export function phasePermits(p: AccessState, capability: Capability): boolean {
  const app = CAPABILITY_APP[capability];
  return app === undefined || appAccess(p, app).mode === 'open';
}

/**
 * The inspections a closed bridge still takes: §3's *special inspections only*,
 * which is a special inspection or one after an event (LC-60).
 */
export const CLOSED_INSPECTION_TYPES: readonly string[] = ['special', 'post-event'];

/**
 * Why a write to this application is refused on a bridge in this state, or
 * null where it is not.
 *
 * THE SERVER'S HALF OF LC-13. The screens withhold the controls through
 * `phasePermits`; functions/lib/phases.ts refuses the request through this, so
 * a client that draws an older table, or none, cannot write history the phase
 * has closed. `inspectionType` is the type being logged or moved, for the one
 * qualifier that is a rule rather than a label: a closed bridge takes special
 * and post-event inspections and nothing else.
 */
export function phaseRefusal(
  p: AccessState,
  app: ProductId,
  opts: { inspectionType?: string | null } = {},
): string | null {
  const line = accessLine(p, app);
  if (line !== null) return line;
  if (
    app === 'inspection' &&
    p.phase === 'closed' &&
    opts.inspectionType != null &&
    !CLOSED_INSPECTION_TYPES.includes(opts.inspectionType)
  ) {
    return `${APP_NAME[app]} takes special and post-event inspections only while this structure is closed.`;
  }
  return null;
}

/** Whether the inspection cycle runs: only for a bridge in service. See derive.ts. */
export function inCycle(p: Pick<PhaseState, 'phase'>): boolean {
  return p.phase === 'in-service';
}

/* --------------------------- the model of record --------------------------- */

export const MODEL_KINDS = ['design', 'as-built'] as const;
export type ModelKind = (typeof MODEL_KINDS)[number];

/** The model a bridge is taken to be, as `Bridge.modelOfRecord` carries it. */
export interface ModelOfRecord {
  modelId: string;
  kind: ModelKind;
  setAt: string;
  setBy: string | null;
  /** The model's current revision (migration 0048); null or absent where it has no export. */
  revision?: string | null;
}

/**
 * The kind a model set by `model.manage` is: design before handover, as-built
 * after. The handover itself sets the as-built (LC-11).
 */
export function kindForPhase(p: Pick<PhaseState, 'phase'>): ModelKind {
  return p.phase === 'planned' || p.phase === 'construction' ? 'design' : 'as-built';
}

/**
 * Whether a record was made on a model that is no longer the model of record,
 * or on a revision of it since imported again (migration 0048). A record with
 * no model id, or a bridge with none, says nothing either way; nor does a
 * revision either side does not know.
 */
export function onSupersededModel(
  recordModelId: string | null | undefined,
  current: ModelOfRecord | null | undefined,
  recordRevision?: string | null,
): boolean {
  if (!recordModelId || !current) return false;
  if (recordModelId !== current.modelId) return true;
  return !!recordRevision && !!current.revision && recordRevision !== current.revision;
}

/** A revision as people read it: its first twelve characters. */
export function shortRevision(revision: string): string {
  return revision.slice(0, 12);
}
